import { createElement } from 'react';
import {
  LayoutDashboard,
  Boxes,
  Receipt,
  Megaphone,
  Users,
  UsersRound,
  UserCog,
  CalendarOff,
  Briefcase,
  CalendarClock,
  FileUser,
  Ticket,
  BellRing,
  Gauge,
  CalendarDays,
  ClipboardList,
  ArrowLeftRight,
  BadgeDollarSign,
  Warehouse,
  Server,
  PackagePlus,
  FileText,
  FileSignature,
  Store,
  Share2,
  Percent,
  FileStack,
  Handshake,
  BarChart3,
  HardDrive,
  FolderKanban,
  Puzzle,
  Gavel,
  ListChecks,
  Mail,
  MessagesSquare,
  ImageDown,
  BookOpen,
  Settings,
  Building2,
  CalendarRange,
  Radar,
  Globe,
  ShieldAlert,
} from 'lucide-react';
import type { NavItem } from '@vnetwork/ui/shells';
import { env } from '@/config/env';

/**
 * Sidebar navigation config — mirrors the datacentral-client-v4 sidebar IA
 * (`src/components/Sidebar/constants.tsx`), on the v2 DashboardLayout shell.
 * Paths match the legacy `paths.*` values so routes line up as modules are
 * ported. Labels are i18n keys under `menu.*`, resolved at render (see AppLayout).
 * Permission gating (rsname/scopes) from the legacy menu is intentionally
 * deferred — add it once the authz layer lands.
 */
export interface WorkspaceNavItem extends NavItem {
  /** i18n key under the `menu.*` namespace. */
  labelKey: string;
  /** Route path (leaf) or '' for a pure group. External items use a full URL. */
  path?: string;
  /** Open in a new tab (external URL, e.g. docs). */
  external?: boolean;
  items?: WorkspaceNavItem[];
}

const icon = (i: typeof LayoutDashboard, size = 18) => createElement(i, { size });

export const NAV: WorkspaceNavItem[] = [
  { id: 'dashboard', labelKey: 'menu.dashboard', path: '/', label: '', icon: icon(LayoutDashboard) },
  { id: 'services', labelKey: 'menu.services', path: '/services', label: '', icon: icon(Boxes) },
  { id: 'services-sla-dashboard', labelKey: 'menu.services_sla_dashboard', path: '/services/sla-dashboard', label: '', icon: icon(Gauge) },
  { id: 'invoices', labelKey: 'menu.invoices', path: '/invoices', label: '', icon: icon(Receipt) },
  {
    id: 'capacities', labelKey: 'menu.capacities', label: '', icon: icon(Warehouse),
    items: [
      { id: 'capacities-overview', labelKey: 'menu.capacities_overview', path: '/capacities', label: '', icon: icon(Gauge) },
      { id: 'capacities-inventory', labelKey: 'menu.capacities_inventory', path: '/capacities/items', label: '', icon: icon(HardDrive) },
      { id: 'capacities-racks', labelKey: 'menu.capacities_racks', path: '/capacities/racks', label: '', icon: icon(Server) },
      { id: 'capacities-processes', labelKey: 'menu.capacities_processes', path: '/capacities/processes', label: '', icon: icon(PackagePlus) },
      { id: 'capacities-models', labelKey: 'menu.capacities_models', path: '/capacities/models', label: '', icon: icon(Boxes) },
      { id: 'capacities-suppliers', labelKey: 'menu.capacities_suppliers', path: '/capacities/suppliers', label: '', icon: icon(Store) },
      { id: 'capacities-locations', labelKey: 'menu.capacities_locations', path: '/capacities/locations', label: '', icon: icon(Warehouse) },
    ],
  },
  { id: 'contacts', labelKey: 'menu.contacts', path: '/contacts', label: '', icon: icon(Megaphone) },
  { id: 'customers', labelKey: 'menu.customers', path: '/customers', label: '', icon: icon(Users) },
  {
    id: 'hrm', labelKey: 'menu.hrm', label: '', icon: icon(UsersRound),
    items: [
      { id: 'employees', labelKey: 'menu.employees', path: '/employees', label: '', icon: icon(UserCog) },
      { id: 'dayoffs', labelKey: 'menu.dayoffs', path: '/dayoffs', label: '', icon: icon(CalendarOff) },
      { id: 'businesses', labelKey: 'menu.businesses', path: '/businesses', label: '', icon: icon(Briefcase) },
      { id: 'workdays', labelKey: 'menu.workdays', path: '/workdays/manage', label: '', icon: icon(CalendarClock) },
      { id: 'resume', labelKey: 'menu.resume', path: '/resume', label: '', icon: icon(FileUser) },
      { id: 'jobs', labelKey: 'menu.jobs', path: '/jobs', label: '', icon: icon(Briefcase) },
    ],
  },
  { id: 'tickets', labelKey: 'menu.tickets', path: '/tickets', label: '', icon: icon(Ticket) },
  {
    id: 'oncall', labelKey: 'menu.oncall', label: '', icon: icon(BellRing),
    items: [
      { id: 'oncall-dashboard', labelKey: 'menu.oncall_dashboard', path: '/oncall', label: '', icon: icon(Gauge) },
      { id: 'oncall-schedules', labelKey: 'menu.oncall_schedules', path: '/oncall/schedules', label: '', icon: icon(CalendarDays) },
      { id: 'oncall-assignments', labelKey: 'menu.oncall_assignments', path: '/oncall/assignments', label: '', icon: icon(ClipboardList) },
      { id: 'oncall-swaps', labelKey: 'menu.oncall_swaps', path: '/oncall/swaps', label: '', icon: icon(ArrowLeftRight) },
      { id: 'oncall-compensations', labelKey: 'menu.oncall_compensations', path: '/oncall/compensations', label: '', icon: icon(BadgeDollarSign) },
    ],
  },
  {
    id: 'contracts', labelKey: 'menu.contracts', label: '', icon: icon(FileText),
    items: [
      { id: 'contracts-customer', labelKey: 'menu.contracts_customer', path: '/contracts', label: '', icon: icon(FileText) },
      { id: 'contracts-partner', labelKey: 'menu.contracts_partner', path: '/contracts/partners', label: '', icon: icon(FileSignature) },
      { id: 'resellers', labelKey: 'menu.resellers', path: '/resellers', label: '', icon: icon(Store) },
      { id: 'contracts-sample', labelKey: 'menu.contracts_sample', path: '/contracts/samples', label: '', icon: icon(FileStack) },
      { id: 'contracts-negotiation', labelKey: 'menu.contracts_negotiation', path: '/contracts/negotiations', label: '', icon: icon(Handshake) },
    ],
  },
  {
    id: 'affiliates', labelKey: 'menu.affiliates', label: '', icon: icon(Share2),
    items: [
      { id: 'affiliates-customer', labelKey: 'menu.affiliates_customer', path: '/affiliates', label: '', icon: icon(Users) },
      { id: 'affiliates-employees', labelKey: 'menu.affiliates_employees', path: '/affiliates/employees', label: '', icon: icon(UserCog) },
    ],
  },
  { id: 'promos', labelKey: 'menu.promos', path: '/promos', label: '', icon: icon(Percent) },
  {
    id: 'reports', labelKey: 'menu.reports', label: '', icon: icon(BarChart3),
    items: [
      { id: 'report-customer', labelKey: 'menu.report_customer', path: '/reports/customers', label: '' },
      { id: 'report-service', labelKey: 'menu.report_service', path: '/reports/services', label: '' },
      { id: 'report-income', labelKey: 'menu.report_income', path: '/reports/incomes', label: '' },
      { id: 'report-revenue', labelKey: 'menu.report_revenue', path: '/reports/revenues', label: '' },
      { id: 'report-business-targets', labelKey: 'menu.report_business_targets', path: '/reports/business-targets', label: '' },
      { id: 'report-targets-analytics', labelKey: 'menu.report_targets_analytics', path: '/reports/targets-analytics', label: '' },
      { id: 'report-financials', labelKey: 'menu.report_financials', path: '/reports/financials', label: '' },
      { id: 'report-service-analytics', labelKey: 'menu.report_service_analytics', path: '/reports/analytics', label: '' },
    ],
  },
  { id: 'drive', labelKey: 'menu.drive', path: '/drive', label: '', icon: icon(HardDrive) },
  { id: 'assets', labelKey: 'menu.assets', path: '/assets', label: '', icon: icon(FolderKanban) },
  {
    id: 'extension', labelKey: 'menu.extension', label: '', icon: icon(Puzzle),
    items: [
      // Operations pages (still routed at /operations/*) parked here for now.
      { id: 'operations-dashboard', labelKey: 'menu.operations_dashboard', path: '/operations', label: '', icon: icon(LayoutDashboard) },
      { id: 'operations-services', labelKey: 'menu.operations_services', path: '/operations/services', label: '', icon: icon(Server) },
      { id: 'operations-instances', labelKey: 'menu.operations_instances', path: '/operations/instances', label: '', icon: icon(HardDrive) },
      { id: 'operations-domains', labelKey: 'menu.operations_domains', path: '/operations/domains', label: '', icon: icon(Globe) },
      { id: 'operations-cdn', labelKey: 'menu.operations_cdn', path: '/operations/cdn', label: '', icon: icon(Radar) },
      { id: 'operations-orgs', labelKey: 'menu.operations_orgs', path: '/operations/orgs', label: '', icon: icon(Building2) },
      { id: 'report-waf', labelKey: 'menu.report_waf', path: '/technical/report', label: '', icon: icon(ShieldAlert) },
      { id: 'reconciliation', labelKey: 'menu.reconciliation', path: '/reconciliation', label: '', icon: icon(CalendarRange) },
      { id: 'tenders', labelKey: 'menu.tenders', path: '/tenders', label: '', icon: icon(Gavel) },
      { id: 'surveys', labelKey: 'menu.surveys', path: '/surveys', label: '', icon: icon(ListChecks) },
      { id: 'mailers', labelKey: 'menu.mailers', path: '/mailers/campaigns', label: '', icon: icon(Mail) },
      { id: 'livechat', labelKey: 'menu.livechat', path: '/livechat', label: '', icon: icon(MessagesSquare) },
      { id: 'tools-webp', labelKey: 'menu.tools_webp', path: '/tools/convert-webp', label: '', icon: icon(ImageDown) },
    ],
  },
  {
    id: 'settings', labelKey: 'menu.settings', label: '', icon: icon(Settings),
    items: [
      { id: 'settings-service-templates', labelKey: 'menu.settings_service_templates', path: '/settings/service-templates', label: '' },
      { id: 'settings-sla-templates', labelKey: 'menu.settings_sla_templates', path: '/settings/sla-templates', label: '' },
      { id: 'settings-regulations', labelKey: 'menu.settings_regulations', path: '/settings/regulations', label: '' },
      { id: 'settings-product-service', labelKey: 'menu.settings_product_service', path: '/settings/product-services', label: '' },
      { id: 'settings-data-access', labelKey: 'menu.settings_data_access', path: '/settings/data-access', label: '' },
    ],
  },
  // Account sub-pages moved to the topbar account menu (see UserMenu) to keep
  // the sidebar to actual product modules.
  { id: 'user-guide', labelKey: 'menu.user_guide', path: env.docsUrl, external: true, label: '', icon: icon(BookOpen) },
];

/** Flattened leaf items (have a real in-app path) — for active-state + ⌘K. */
export const NAV_LEAVES: WorkspaceNavItem[] = NAV.flatMap((item) =>
  item.items ? item.items : [item],
).filter((item) => item.path && !item.external);
